import React, { useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CircularProgress from '@mui/material/CircularProgress'
import { useRoutingContext } from '../../hooks/use-routing-context'
import RouteSummaryList from '@/javascript/presentation/pages/routing/create/distribution/route-summary-list'
import RouteMapView from '@/javascript/presentation/pages/routing/components/route-map-view'
import { RouteSuggestion } from '@/javascript/domain/entities/route-suggestion'
import { Typography } from '@mui/material'
import AlertWithTooltip from '@/javascript/presentation/components/alert-with-tooltip'

export const StepDistribution: React.FC = () => {
  const {
    setActiveStep, routeSuggestions, unassignedWorkIds, unassignedMemberIds,
    selectedMembers, selectedWorks, memberConfigurations, createNewRoute,
    setSelectionError, date
  } = useRoutingContext()
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const memberById = new Map(selectedMembers.map(m => [m.id.toString(), { ...m, configuration: memberConfigurations[m.id] ?? null }]))

  const memberNameById = useMemo(
    () => new Map(selectedMembers.map(m => [m.id.toString(), m.user.completeData])),
    [selectedMembers]
  )

  const workById = useMemo(
    () => new Map(selectedWorks.map(w => [w.id.toString(), w])),
    [selectedWorks]
  )

  const handleToggle = (index: number, isExpanded: boolean) => {
    setExpandedIndex(isExpanded ? index : null)
  }

  const handleConfirm = async () => {
    setIsLoading(true)
    setSelectionError(null)
    try {
      await createNewRoute.create({ date, memberById, routeSuggestions })
      window.location.href = '/work_team_maps?tab=1&date=' + date.format('YYYY-MM-DD')
    } catch (error) {
      setSelectionError({ message: error.message, reason: error.reason, references: error.references })
      setIsLoading(false)
    }
  }

  const unassignedWorksMessage = unassignedWorkIds.length === 1
    ? '1 atividade não foi roteirizada'
    : `${unassignedWorkIds.length} atividades não foram roteirizadas`

  const unassignedMembersMessage = unassignedMemberIds.length === 1
    ? '1 executor não foi incluído'
    : `${unassignedMemberIds.length} executores não foram incluídos`

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="route-distribution">
      {unassignedWorkIds.length > 0 && (
        <AlertWithTooltip
          message={unassignedWorksMessage}
          tooltipContent={<>
            <Typography><strong>Uma atividade pode não ser roteirizada quando:</strong></Typography>
            <ul>
              <li><Typography>Executores não possuem permissão ao formulário utilizado nas atividades.</Typography></li>
              <li><Typography>A permissão &quot;Executar atividades de clientes/locais/ativos que não é responsável&quot; está desativada.</Typography></li>
              <li><Typography>A capacidade máxima definida para os executores é menor do que a quantidade de atividades selecionadas.</Typography></li>
            </ul>
          </>}
        />
      )}
      {unassignedMemberIds.length > 0 && (
        <AlertWithTooltip
          message={unassignedMembersMessage}
          tooltipContent={<>
            <Typography><strong>Um executor pode não ser incluído quando:</strong></Typography>
            <ul>
              <li><Typography>Não possuem permissão ao formulário utilizado nas atividades.</Typography></li>
              <li><Typography>A permissão &quot;Executar atividades de clientes/locais/ativos que não é responsável&quot; está desativada.</Typography></li>
              <li><Typography>Não foram encontradas atividades compatíveis com as configurações do executor.</Typography></li>
            </ul>
          </>}
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ width: { xs: '100%', md: '50%' }, height: { xs: 300, md: '70vh' } }}>
          <RouteMapView routeSuggestions={routeSuggestions} expandedIndex={expandedIndex} />
        </Box>
        <Box sx={{ flex: 1, gap: 2, overflowY: 'auto', maxHeight: { xs: 400, md: '70vh' } }}>
          <RouteSummaryList
            suggestions={routeSuggestions}
            memberNameById={memberNameById}
            workById={workById}
            expandedIndex={expandedIndex}
            onToggle={handleToggle}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
        <Button variant='outlined' onClick={() => setActiveStep(0)}
          startIcon={<ChevronLeftIcon />}
        >
          Anterior
        </Button>
        <Button variant='contained' onClick={handleConfirm}
          startIcon={isLoading && <CircularProgress color="inherit" size={16} />}
          disabled={isLoading || routeSuggestions.length === 0}
          endIcon={!isLoading && routeSuggestions.length > 0 && <CheckCircleIcon />}
        >
          Confirmar roteirização
        </Button>
      </Box>
    </Box>
  )
}
